import React, { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Fullscreen photo viewer. Opens when `index` is a number, closes on
// ESC / backdrop / X. Arrow keys navigate; swipe works on touch devices.
export const PhotoLightbox = ({ gallery = [], index, onClose, onChange, title }) => {
  const open = typeof index === "number" && index >= 0 && gallery.length > 0;

  const prev = useCallback(() => {
    onChange((index - 1 + gallery.length) % gallery.length);
  }, [index, gallery.length, onChange]);

  const next = useCallback(() => {
    onChange((index + 1) % gallery.length);
  }, [index, gallery.length, onChange]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, prev, next, onClose]);

  // Swipe support
  const touch = React.useRef({ x: 0 });
  const onTouchStart = (e) => { touch.current.x = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.current.x;
    if (dx > 50) prev();
    else if (dx < -50) next();
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title ? `Foto's van ${title}` : "Foto's"}
      data-testid="photo-lightbox"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Counter */}
      <div className="pointer-events-none absolute left-4 top-4 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
        {index + 1} / {gallery.length}
      </div>

      {/* Close */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        aria-label="Sluit"
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
      >
        <X size={20} />
      </button>

      {/* Prev */}
      {gallery.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); prev(); }}
          data-testid="lightbox-prev"
          aria-label="Vorige"
          className="absolute left-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
        >
          <ChevronLeft size={22} />
        </button>
      )}

      {/* Image */}
      <img
        src={gallery[index]}
        alt={title || `Foto ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
        data-testid="lightbox-image"
      />

      {/* Next */}
      {gallery.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); next(); }}
          data-testid="lightbox-next"
          aria-label="Volgende"
          className="absolute right-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
        >
          <ChevronRight size={22} />
        </button>
      )}

      {/* Thumbnails strip */}
      {gallery.length > 1 && (
        <div
          className="pointer-events-auto absolute bottom-3 left-1/2 flex max-w-[94vw] -translate-x-1/2 gap-2 overflow-x-auto rounded-xl bg-white/10 p-2 backdrop-blur"
          onClick={(e) => e.stopPropagation()}
        >
          {gallery.map((g, i) => (
            <button
              key={i}
              onClick={() => onChange(i)}
              data-testid={`lightbox-thumb-${i}`}
              className={`h-14 w-20 shrink-0 overflow-hidden rounded-md border-2 transition ${i === index ? "border-white" : "border-transparent opacity-70 hover:opacity-100"}`}
            >
              <img src={g} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
